'use client'

import { ThemeProvider } from '@repo/core/theme'
import { IntlProvider, useTranslations, type AbstractIntlMessages } from '@repo/core/i18n'
import { useState, useMemo, useEffect, useRef } from 'react'
import {
  MainServiceProvider,
  WebSocketMachineProvider,
  useMainService,
  useConnection,
  useCurrentAccount,
} from '@repo/api-client'
import { TooltipProvider } from '@repo/ui'
import { AuthMachineProvider, AuthListMachineProvider } from '@repo/auth'
import { AnalyticsProvider } from '@repo/core'
import {
  ServiceRegistry,
  ProfileService,
  ChatService,
  EngineCallServiceAdapter,
  ContactService,
  NotificationService,
  HRService,
  DocumentService,
  MediaService,
  BillingAppService,
  WalletAppService,
  EntityAdminService,
  LimsAppService,
  CdnAppService,
  DocVerificationService,
  TtsApiService,
  ServiceRuntimeConfig,
} from '@repo/services'
import { createNexusConfig, createAnalyticsConfig, getAppName, brandMessages, fetchEntityInfo } from '@/config'
import { CookieConsent } from '@/shared/ui/components/cookie-consent'
import { ErrorPage } from '@/widgets/error-page'
import { getLocale } from '@/i18n/client'
import type { Locale } from '@/i18n/config'
import { NexusConfigProvider } from '@/providers/nexus-config-provider'
import { ErrorBoundary } from '@repo/ui'
import { UIMachineProvider, NavigationMachineProvider } from '@/machines'
import { ChannelListMachineProvider, MessageListMachineProvider } from '@repo/chat'
import { CrmMachineProviders } from '@/shared/ui/providers/crm-machine-provider'
import {
  ContactListMachineProvider,
  getContactsStoreSnapshot,
  findContactByAccountId,
  getContactDisplayName,
} from '@repo/contacts'

// Import messages statically
import enMessages from '../../messages/en.json'
import ruMessages from '../../messages/ru.json'
import tgMessages from '../../messages/tg.json'

const messagesMap: Record<Locale, any> = {
  en: enMessages,
  ru: ruMessages,
  tg: tgMessages,
}

/**
 * ServiceInitializer - Initializes application services when MainService is ready
 * This component handles:
 * - Registering services with ServiceRegistry
 * - Initializing services when WebSocket is connected
 * - Cleaning up services on disconnect/account change
 */
function ServiceInitializer({ children }: { children: React.ReactNode }) {
  const mainService = useMainService()
  const { isConnected } = useConnection()
  const currentAccount = useCurrentAccount()
  const tErrors = useTranslations('errors')
  const initRef = useRef(false)
  const servicesReadyRef = useRef(false)
  const [initError, setInitError] = useState<Error | null>(null)

  useEffect(() => {
    if (!mainService || initRef.current) return

    ServiceRuntimeConfig.configure({
      enableMockServices: import.meta.env['VITE_ENABLE_MOCK_SERVICES'] === 'true',
    })

    // Set MainService in registry
    ServiceRegistry.setMainService(mainService)

    // Register ProfileService FIRST (other services depend on it)
    const profileService = new ProfileService(mainService)
    // Wire contacts resolver so ProfileService can resolve names from address book
    // when backend profile has no name data (avoids "User #XXXX" fallback)
    profileService.setContactsResolver((accountId) => {
      const snap = getContactsStoreSnapshot()
      if (!snap?.contacts?.length) return undefined
      const contact = findContactByAccountId(snap.contacts, accountId)
      return contact ? getContactDisplayName(contact) : undefined
    })
    ServiceRegistry.register('profile', profileService)

    // Register services that depend on ProfileService
    // Pass shared profileService instance to avoid duplicate initialization
    const chatService = new ChatService(mainService, profileService)
    ServiceRegistry.register('chat', chatService)

    const callService = new EngineCallServiceAdapter(mainService, profileService)
    ServiceRegistry.register('call', callService)
    ServiceRegistry.register('contacts', ContactService)
    ServiceRegistry.register('notifications', NotificationService)

    // Register services that previously used module-level singletons/factories
    ServiceRegistry.register('hr', HRService)
    ServiceRegistry.register('documents', DocumentService)
    ServiceRegistry.register('media', MediaService)
    ServiceRegistry.register('billing', BillingAppService)
    ServiceRegistry.register('wallet', WalletAppService)
    ServiceRegistry.register('entities', EntityAdminService)
    ServiceRegistry.register('lims', LimsAppService)
    ServiceRegistry.register('cdn', CdnAppService)
    ServiceRegistry.register('doc-verification', DocVerificationService)
    ServiceRegistry.register('tts', TtsApiService)

    initRef.current = true
  }, [mainService])

  useEffect(() => {
    const accountId = currentAccount?.accountId

    if (!isConnected || !accountId) {
      // Don't dispose services on temporary disconnects
      // Only dispose if account changes or is explicitly removed
      if (!accountId && servicesReadyRef.current) {
        console.log('[ServiceInitializer] Account removed, disposing services')
        ServiceRegistry.disposeAll()
        servicesReadyRef.current = false
        setInitError(null)
      }
      return
    }

    // If services already initialized, this is a reconnect — refresh data
    if (servicesReadyRef.current) {
      console.log('[ServiceInitializer] Reconnected, refreshing data...')
      try {
        // Reset contacts sync flag and reload so reconnect triggers fresh sync
        const contactService = ServiceRegistry.get<ContactService>('contacts')
        contactService.resetSyncFlag()
        contactService.loadContacts().catch(() => {})
        ServiceRegistry.get<ChatService>('chat').loadChannels?.().catch?.(() => {})
      } catch (e) {
        console.warn('[ServiceInitializer] Reconnect refresh failed:', e)
      }
      return
    }

    console.log('[ServiceInitializer] Connected, initializing services...', {
      isConnected,
      accountId,
    })

    // Initialize services when connected
    async function initServices() {
      try {
        // CRITICAL: Set account ID on ProfileService FIRST (other services depend on it)
        const profileService = ServiceRegistry.get<ProfileService>('profile')
        profileService.setCurrentAccount(accountId ?? null)

        // Update account ID in other services
        const chatService = ServiceRegistry.get<ChatService>('chat')
        chatService.setCurrentAccount(accountId ?? null)

        const callService = ServiceRegistry.get<EngineCallServiceAdapter>('call')
        callService.setCurrentAccount(accountId ?? null)

        const contactService = ServiceRegistry.get<ContactService>('contacts')
        // ContactService doesn't have setCurrentAccount, it uses mainService directly

        console.log('[ServiceInitializer] Calling ServiceRegistry.initializeAll()...')

        // Initialize all services (ProfileService first, then others)
        await ServiceRegistry.initializeAll()
        servicesReadyRef.current = true
        setInitError(null)

        console.log('[ServiceInitializer] All services initialized successfully')
      } catch (error) {
        servicesReadyRef.current = false
        setInitError(error instanceof Error ? error : new Error(String(error)))
        console.error('[ServiceInitializer] Failed to initialize services:', error)
      }
    }

    initServices()
  }, [isConnected, currentAccount?.accountId])

  if (initError) {
    const failedServices = ServiceRegistry.getHealth()
      .failedServices
      .map((service) => service.name)
      .join(', ')

    const message = failedServices
      ? `${tErrors('serviceInitializationMessage')} (${failedServices})`
      : tErrors('serviceInitializationMessage')

    return (
      <ErrorPage
        error={initError}
        title={tErrors('serviceInitializationFailed')}
        message={message}
        onReset={() => window.location.reload()}
        showResetButton
        showHomeLink={false}
      />
    )
  }

  return <>{children}</>
}

function NexusSyncProvider({ children }: { children: React.ReactNode }) {
  return (
    <ServiceInitializer>
      <ChannelListMachineProvider>
        <MessageListMachineProvider>
          <ContactListMachineProvider>
            <CrmMachineProviders>
              <NavigationMachineProvider>
                <NexusConfigProvider>{children}</NexusConfigProvider>
              </NavigationMachineProvider>
            </CrmMachineProviders>
          </ContactListMachineProvider>
        </MessageListMachineProvider>
      </ChannelListMachineProvider>
    </ServiceInitializer>
  )
}

export function Providers({ children }: { children: React.ReactNode }) {
  const nexusConfig = useMemo(() => createNexusConfig(), [])
  const analyticsConfig = useMemo(() => createAnalyticsConfig(), [])
  // No SSR — safe to read cookie synchronously on first render
  const [locale] = useState<Locale>(() => getLocale())

  // App name: cached entity → subdomain fallback. Updated when /v1/entity/me responds.
  const [appName, setAppName] = useState(() => getAppName())
  const messages = useMemo(() => brandMessages(messagesMap[locale], appName), [locale, appName])

  // Fetch entity info from server, update branding when ready
  useEffect(() => {
    fetchEntityInfo().then((entity) => {
      if (entity?.name) {
        const name = entity.name.charAt(0).toUpperCase() + entity.name.slice(1).toLowerCase()
        setAppName(name)
        document.title = name
      }
    })
  }, [])

  // Sync document title on initial render (from cache/subdomain)
  useEffect(() => {
    document.title = appName
  }, [appName])

  return (
    <ErrorBoundary title="Something went wrong" description="Error Details" tryAgainLabel="Try Again">
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
        <IntlProvider locale={locale} messages={messages}>
          <AnalyticsProvider config={analyticsConfig}>
            <MainServiceProvider config={nexusConfig} autoConnect={false}>
                <WebSocketMachineProvider>
                  <AuthListMachineProvider>
                    <NexusSyncProvider>
                      <AuthMachineProvider>
                        <UIMachineProvider>
                          <TooltipProvider delayDuration={0}>
                            {children}
                            <CookieConsent />
                          </TooltipProvider>
                        </UIMachineProvider>
                      </AuthMachineProvider>
                    </NexusSyncProvider>
                  </AuthListMachineProvider>
                </WebSocketMachineProvider>
            </MainServiceProvider>
          </AnalyticsProvider>
        </IntlProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
